import { useId, useState, type ReactNode } from 'react';

interface FormSectionProps {
  title: string;
  /** One line under the heading. */
  description?: string;
  /** Collapsible sections start closed unless `defaultOpen`. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}

/**
 * One titled group of a long entity form (brand, manufacturer). The heading
 * style follows the Reference editor's section headings; a collapsible section
 * is for fields kept for compatibility rather than day-to-day editing.
 */
export function FormSection({ title, description, collapsible = false, defaultOpen = false, children }: FormSectionProps) {
  const [open, setOpen] = useState(!collapsible || defaultOpen);
  const bodyId = useId();
  return (
    <section className='border-t border-gray-200 pt-4 first:border-t-0 first:pt-0'>
      {collapsible ? (
        <button
          type='button'
          className='flex w-full items-center justify-between text-left'
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((v) => !v)}
        >
          <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>{title}</h3>
          <span className='text-xs text-gray-400'>{open ? 'Hide' : 'Show'}</span>
        </button>
      ) : (
        <h3 className='text-sm font-semibold uppercase tracking-wide text-gray-500'>{title}</h3>
      )}
      {description && <p className='mt-1 text-xs text-gray-500'>{description}</p>}
      {open && (
        <div id={bodyId} className='mt-3 space-y-4'>
          {children}
        </div>
      )}
    </section>
  );
}
